/** @format */

/* eslint require-await: "warn" */

/* eslint react-hooks/rules-of-hooks: "warn" */
import {
    BaseIconProps,
    ButtonIconProps,
} from '@mpb-com/feather/components/atoms/icon';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext } from 'react';

import { MarketContext } from '@toucan/client/global/market';
import { WrappedLink } from '@toucan/client/global/nextjs';
import { UserContext } from '@toucan/client/global/user';

import { ToggleFavourite } from '../../../toggle-favourite';
import {
    getButtonLabel,
    getSavedProductsUrl,
} from './favourite-product-button.helpers';

export type FavouriteProductButtonProps = {
    isLoading?: boolean;
    productId?: number;
} & (BaseIconProps | ButtonIconProps);

export const FavouriteProductButton: FunctionComponent<
    FavouriteProductButtonProps
> = ({ isLoading, productId, ...iconProps }: FavouriteProductButtonProps) => {
    if (!isLoading && !productId) {
        return null;
    }

    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const { addSavedProduct, removeSavedProduct, isSavedProduct } =
        useContext(UserContext);

    const isSaved = isSavedProduct(productId);

    async function handleToggleSaveProduct() {
        if (!productId) {
            return;
        }

        if (isSaved) {
            removeSavedProduct(productId);
            return;
        }

        addSavedProduct(productId);
    }

    const renderTooltipSuccessContent = () => {
        const successComponents = [
            <WrappedLink
                key={0}
                href="/account/saved-products"
                passHref={true}
                as={getSavedProductsUrl({ activeMarket, t })}
            >
                $1
            </WrappedLink>,
        ];

        return (
            <Trans components={successComponents}>
                {t('Product saved. <0>See all your saved products</0>.', {
                    context: 'favouriteProduct',
                })}
            </Trans>
        );
    };

    return (
        <ToggleFavourite
            onToggle={handleToggleSaveProduct}
            isFavourite={isSaved}
            successMessage={renderTooltipSuccessContent()}
            label={getButtonLabel({ isSaved, t })}
            isLoading={isLoading}
            data-testid="favourite-product-button"
            {...iconProps}
        />
    );
};
